import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trash2, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { LEAD_STATUSES, STATUS_COLORS } from "@/data/content";

function Field({ label, children }) {
    return (
        <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
            <p className="mt-1 text-sm text-slate-200 break-words">{children || <span className="text-slate-600">—</span>}</p>
        </div>
    );
}

export default function AdminLeadDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [lead, setLead] = useState(null);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get(`/admin/inquiries/${id}`).then((r) => setLead(r.data)).catch(() => setLead(false));
    }, [id]);

    if (lead === null) return <p className="py-20 text-center text-sm text-slate-500">Loading…</p>;
    if (lead === false) return <p className="py-20 text-center text-sm text-slate-500" data-testid="lead-not-found">Inquiry not found.</p>;

    const setStatus = async (status) => {
        try {
            const { data } = await api.patch(`/admin/inquiries/${id}`, { status });
            setLead(data);
            toast.success(`Status updated to ${status}`);
        } catch (e) { toast.error(errMsg(e)); }
    };

    const addNote = async (e) => {
        e.preventDefault();
        if (!note.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.patch(`/admin/inquiries/${id}`, { note });
            setLead(data);
            setNote("");
            toast.success("Note added");
        } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
    };

    const remove = async () => {
        if (!window.confirm(`Delete inquiry ${lead.reference}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/inquiries/${id}`);
            toast.success("Inquiry deleted");
            navigate("/admin/leads");
        } catch (e) { toast.error(errMsg(e)); }
    };

    return (
        <div data-testid="admin-lead-detail">
            <Link to="/admin/leads" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-white" data-testid="lead-back-link">
                <ArrowLeft className="h-4 w-4" /> All inquiries
            </Link>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl font-bold text-white">{lead.full_name}</h1>
                    <p className="mt-1 font-mono2 text-xs text-slate-500" data-testid="lead-reference">{lead.reference} · {new Date(lead.created_at).toLocaleString()}</p>
                    {lead.email_status === "sent" && (
                        <p className="mt-2 inline-block rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[11px] font-semibold text-emerald-300" data-testid="lead-email-status-sent">Owner notified by email</p>
                    )}
                    {lead.client_email_status === "sent" && (
                        <p className="mt-2 ml-2 inline-block rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold text-cyan-300" data-testid="lead-client-email-sent">Client confirmation sent</p>
                    )}
                    {lead.client_email_status === "failed" && (
                        <p className="mt-2 ml-2 inline-block rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1 text-[11px] font-semibold text-rose-300" data-testid="lead-client-email-failed">Client confirmation failed — inquiry is saved</p>
                    )}
                    {lead.email_status === "failed" && (
                        <p className="mt-2 inline-block rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1 text-[11px] font-semibold text-rose-300" data-testid="lead-email-status-failed">Email notification failed — inquiry is saved</p>
                    )}
                    {(lead.email_status === "skipped" || lead.email_status === "disabled") && (
                        <p className="mt-2 inline-block rounded-full border border-slate-500/30 bg-slate-500/10 px-3 py-1 text-[11px] font-semibold text-slate-400" data-testid="lead-email-status-off">Email notification off</p>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <select value={lead.status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-lead-status-select"
                        className={`rounded-xl border px-4 py-2.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-violet-500 bg-[#0F1420] ${STATUS_COLORS[lead.status] || ""}`}>
                        {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={remove} data-testid="admin-lead-delete-button" aria-label="Delete inquiry"
                        className="rounded-xl border border-rose-500/30 p-2.5 text-rose-400 transition-colors hover:bg-rose-500/10">
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-3">
                <div className="space-y-6 rounded-2xl border border-white/10 bg-hive-card p-6 lg:col-span-2">
                    <div className="grid gap-5 sm:grid-cols-2">
                        <Field label="Email"><a href={`mailto:${lead.email}`} className="text-cyan-300 hover:underline" data-testid="lead-email">{lead.email}</a></Field>
                        <Field label="Phone">{lead.phone}</Field>
                        <Field label="Company">{lead.company}</Field>
                        <Field label="Preferred contact">{lead.preferred_contact}</Field>
                        <Field label="Service">{lead.service}</Field>
                        <Field label="Budget">{lead.budget}</Field>
                        <Field label="Timeline">{lead.timeline}</Field>
                        <Field label="Referring page"><span className="font-mono2 text-xs">{lead.referrer_page}</span></Field>
                    </div>
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Project Description</p>
                        <p className="mt-2 whitespace-pre-line rounded-xl border border-white/10 bg-[#0F1420] p-4 text-sm leading-relaxed text-slate-200" data-testid="lead-description">{lead.description}</p>
                    </div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-hive-card p-6">
                    <h2 className="flex items-center gap-2 font-display font-bold text-white"><StickyNote className="h-4 w-4 text-amber-400" /> Internal Notes</h2>
                    <p className="mt-1 text-[11px] text-slate-500">Private — never shown publicly.</p>
                    <form onSubmit={addNote} className="mt-4">
                        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="admin-lead-note-input"
                            placeholder="Add a note…"
                            className="w-full rounded-xl border border-white/10 bg-[#0F1420] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500" />
                        <button type="submit" disabled={busy} data-testid="admin-lead-note-submit"
                            className="mt-3 w-full rounded-xl bg-violet-600 py-2.5 text-sm font-semibold text-white hover:bg-violet-500 disabled:opacity-60">
                            {busy ? "Saving…" : "Add Note"}
                        </button>
                    </form>
                    <ul className="mt-5 space-y-3">
                        {(lead.notes || []).slice().reverse().map((n, i) => (
                            <li key={i} className="rounded-xl border border-white/5 bg-[#0F1420] p-3.5">
                                <p className="text-sm text-slate-200">{n.text}</p>
                                <p className="mt-1.5 text-[10px] text-slate-500">{n.by} · {new Date(n.at).toLocaleString()}</p>
                            </li>
                        ))}
                        {(lead.notes || []).length === 0 && <li className="text-xs text-slate-600">No notes yet.</li>}
                    </ul>
                </div>
            </div>
        </div>
    );
}
